// AG Grid column definitions per detail-grid section. `field` matches the
// section model's detailed_records row keys; `sortable` is set only on columns
// the backend can sort (its DETAIL_SORT / sort whitelist), so column-header
// sort maps cleanly to server-side sort_by.
import React from 'react';
import ProjectStatusRenderer from 'components/app/crm/project/component/ProjectStatusRenderer';

// Coloured pipeline badge. Works for both row shapes: a nested `sales_pipeline`
// object (quotation/job rows) or flat `sales_pipeline_id`/`_color` (sales rows).
const statusBadge = (p) => {
  const d = p.data || {};
  const sp = d.sales_pipeline || { id: d.sales_pipeline_id, color: d.sales_pipeline_color };
  return <ProjectStatusRenderer value={p.value} data={{ sales_pipeline: sp }} />;
};

const num = (p) => (p.value || p.value === 0 ? Number(p.value).toLocaleString('en-US', { maximumFractionDigits: 1 }) : '—');
const money = (p) => {
  const v = Number(p.value || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};
const txt = (p) => (p.value == null || p.value === '' ? '—' : p.value);
const rightNum = { type: 'numericColumn', cellStyle: { textAlign: 'right' } };

// Sales / PCV / Lost share the Sales row shape.
const SALES = [
  { headerName: 'Q / J #', field: 'q_number', minWidth: 100, width: 130, flex: 0, pinned: true, sortable: true, valueGetter: (p) => p.data?.j_number || p.data?.q_number || '—' },
  { headerName: 'Project', field: 'name', flex: 2, minWidth: 160, sortable: true, valueFormatter: txt },
  { headerName: 'Customer', field: 'company_name', flex: 1, minWidth: 120, valueFormatter: txt },
  { headerName: 'Status', field: 'sales_pipeline_name', width: 170, cellRenderer: statusBadge },
  { headerName: 'Value', field: 'amount', width: 110, sortable: true, ...rightNum, valueFormatter: money },
  { headerName: 'Weight (MT)', field: 'weight', width: 120, sortable: true, ...rightNum, valueFormatter: num },
  { headerName: 'Sales Incharge', field: 'sales_incharge', width: 150, sortable: true, valueFormatter: txt },
  { headerName: 'Unit', field: 'sales_unit_name', width: 120, valueFormatter: txt },
  { headerName: 'Country', field: 'pj_country', width: 120, valueFormatter: txt },
  { headerName: 'Source', field: 'from_lead', width: 90, valueGetter: (p) => (p.data?.from_lead ? 'Lead' : 'Direct') },
  { headerName: 'Open Date', field: 'open_date', width: 120, sortable: true, valueFormatter: txt }
];

const QUOTATION = [
  { headerName: 'Q #', field: 'q_number', width: 120, sortable: true, valueFormatter: txt },
  { headerName: 'J #', field: 'j_number', width: 120, sortable: true, valueFormatter: txt },
  { headerName: 'Project', field: 'project_name', flex: 2, minWidth: 160, sortable: true, valueFormatter: txt },
  { headerName: 'Customer', field: 'customer_name', flex: 1, minWidth: 120, sortable: true, valueFormatter: txt },
  { headerName: 'Status', field: 'status_label', width: 150, cellRenderer: statusBadge },
  { headerName: 'Buildings', field: 'building_count', width: 110, sortable: true, ...rightNum, valueFormatter: num },
  { headerName: 'Weight (MT)', field: 'weight', width: 120, sortable: true, ...rightNum, valueFormatter: num },
  { headerName: 'Sales Incharge', field: 'sales_incharge', width: 150, sortable: true, valueFormatter: txt },
  { headerName: 'Unit', field: 'unit_name', width: 120, valueFormatter: txt },
  { headerName: 'Region', field: 'region', width: 120, sortable: true, valueFormatter: txt },
  { headerName: 'Entry Date', field: 'quotation_entry_date', width: 130, sortable: true, valueFormatter: txt }
];

const JOB_ENTRY = [
  { headerName: 'J #', field: 'j_number', width: 120, sortable: true, valueFormatter: txt },
  { headerName: 'Q #', field: 'q_number', width: 120, sortable: true, valueFormatter: txt },
  { headerName: 'Project', field: 'project_name', flex: 2, minWidth: 160, sortable: true, valueFormatter: txt },
  { headerName: 'Customer', field: 'customer_name', flex: 1, minWidth: 120, sortable: true, valueFormatter: txt },
  { headerName: 'Category', field: 'category', width: 110, sortable: true, valueFormatter: txt },
  { headerName: 'Status', field: 'status_label', width: 150, cellRenderer: statusBadge },
  { headerName: 'Weight (MT)', field: 'weight', width: 120, sortable: true, ...rightNum, valueFormatter: num },
  { headerName: 'BOM (MT)', field: 'bom_weight', width: 110, sortable: true, ...rightNum, valueFormatter: num },
  { headerName: 'Sales Incharge', field: 'sales_incharge', width: 150, sortable: true, valueFormatter: txt },
  { headerName: 'Unit', field: 'unit_name', width: 120, valueFormatter: txt },
  { headerName: 'Region', field: 'region', width: 120, sortable: true, valueFormatter: txt },
  { headerName: 'Entry Date', field: 'job_entry_date', width: 130, sortable: true, valueFormatter: txt }
];

const CONTRACT = [
  { headerName: 'Contract #', field: 'contract_number', width: 140, valueFormatter: txt },
  { headerName: 'Project', field: 'project_name', flex: 2, minWidth: 160, sortable: true, valueFormatter: txt },
  { headerName: 'J #', field: 'j_number', width: 120, sortable: true, valueFormatter: txt },
  { headerName: 'Customer', field: 'customer_name', flex: 1, minWidth: 120, valueFormatter: txt },
  { headerName: 'Value', field: 'contract_value', width: 120, sortable: true, ...rightNum, valueFormatter: money },
  { headerName: 'Grand Total', field: 'grand_total_value', width: 120, ...rightNum, valueFormatter: money },
  { headerName: 'Weight (MT)', field: 'contract_weight', width: 120, sortable: true, ...rightNum, valueFormatter: num },
  { headerName: 'Area (m²)', field: 'contract_area', width: 110, sortable: true, ...rightNum, valueFormatter: num },
  { headerName: 'Released', field: 'released', width: 110, valueGetter: (p) => (p.data?.released ? 'Released' : 'Unreleased') },
  { headerName: 'Signed Date', field: 'signed_date', width: 130, sortable: true, valueFormatter: txt },
  { headerName: 'Unit', field: 'unit_name', width: 120, valueFormatter: txt },
  { headerName: 'Region', field: 'region', width: 120, valueFormatter: txt }
];

const WAIVER = [
  { headerName: 'Waiver #', field: 'waiver_number', width: 130, sortable: true, valueFormatter: txt },
  { headerName: 'Project', field: 'project_name', flex: 2, minWidth: 160, sortable: true, valueFormatter: txt },
  { headerName: 'J #', field: 'j_number', width: 120, sortable: true, valueFormatter: txt },
  { headerName: 'Status', field: 'status', width: 150, sortable: true, valueFormatter: txt },
  { headerName: 'Overdue', field: 'overdue', width: 100, valueGetter: (p) => (p.data?.overdue ? 'Yes' : 'No') },
  { headerName: 'Commitment', field: 'commitment_date', width: 130, sortable: true, valueFormatter: txt },
  { headerName: 'Value', field: 'value', width: 120, sortable: true, ...rightNum, valueFormatter: money },
  { headerName: 'Weight (MT)', field: 'weight', width: 120, sortable: true, ...rightNum, valueFormatter: num },
  { headerName: 'Sales Incharge', field: 'sales_incharge', width: 150, sortable: true, valueFormatter: txt },
  { headerName: 'Unit', field: 'unit_name', width: 120, sortable: true, valueFormatter: txt },
  { headerName: 'Region', field: 'region', width: 120, sortable: true, valueFormatter: txt }
];

const DETAIL_COLUMNS = {
  sales:     SALES,
  pcv:       SALES,
  lost:      SALES,
  quotation: QUOTATION,
  job_entry: JOB_ENTRY,
  contract:  CONTRACT,
  waiver:    WAIVER
};

// Section that owns the detail grid for each tab + any forced drill filter.
export const TAB_SECTION = {
  overview:  { section: 'sales' },
  pcv:       { section: 'pcv' },
  quotation: { section: 'quotation' },
  contract:  { section: 'contract' },
  waiver:    { section: 'waiver' },
  lost:      { section: 'lost', extra: { funnel_stage: 'lost' } }
};

export default DETAIL_COLUMNS;
